import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { getHostingVisits } from '../api/client';
import { hostedSites, type HostedSite, type HostingSiteStatus } from '../data/hostingSites';
import { mergeSitesWithStoredVisits } from '../utils/siteVisitCounts';
import { HUB_URL } from '../lib/site';
import PageHeader from '../components/ui/PageHeader';
import HScroller from '../components/ui/HScroller';

type HostingSortMode = 'nameAsc' | 'nameDesc' | 'visitsDesc' | 'visitsAsc';

const THIS_SITE = hostedSites[0].name;
const TOP_VISITED_COUNT = 4;

const STATUS: Record<HostingSiteStatus, { label: string; dot: string; text: string }> = {
  live: { label: 'Live', dot: 'bg-success-500', text: 'text-success-300' },
  partner: { label: 'Reach out to partner', dot: 'bg-primary-400', text: 'text-primary-300' },
  comingSoon: { label: 'Coming soon', dot: 'bg-warning-500', text: 'text-warning-300' },
};

const StatusBadge: React.FC<{ status: HostingSiteStatus }> = ({ status }) => {
  const s = STATUS[status];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap ${s.text}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} aria-hidden="true" />
      {s.label}
    </span>
  );
};

const hostOf = (url?: string) => {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const cardBase = 'block h-full panel p-4 hover:border-primary-400/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';

/** One directory card. Behavior is unchanged from the 1.39 tree: live → external (this site → home), partner → inquiry, coming soon → inert. */
const SiteCard: React.FC<{ site: HostedSite; showVisits?: boolean; className?: string; visitStatus?: 'loading' | 'ready' | 'unavailable' }> = ({
  site,
  showVisits = true,
  className = '',
  visitStatus = 'ready',
}) => {
  const isLive = site.status === 'live' && !!site.url;
  const isThisSite = site.name === THIS_SITE;
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-white leading-snug">{site.name.trim()}</h3>
        <StatusBadge status={site.status} />
      </div>
      {isLive && (
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-primary-300 min-w-0">
          <span className="truncate">{isThisSite ? 'This site' : hostOf(site.url)}</span>
          {!isThisSite && <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />}
        </p>
      )}
      <p className="mt-2 text-sm text-secondary-300 line-clamp-3">{site.description}</p>
      {site.status === 'partner' && <p className="mt-2 text-sm font-semibold text-primary-300">Send a hosting inquiry →</p>}
      {showVisits && site.status === 'live' && visitStatus === 'ready' && (
        <p className="mt-2 text-xs text-secondary-400">{site.monthlyVisits.toLocaleString()} visits</p>
      )}
      {showVisits && site.status === 'live' && visitStatus === 'unavailable' && (
        <p className="mt-2 text-xs text-secondary-500">Visit totals unavailable</p>
      )}
    </>
  );

  if (isLive && isThisSite) {
    return (
      <Link to="/" className={`${cardBase} ${className}`}>
        {inner}
      </Link>
    );
  }
  if (isLive) {
    return (
      <a href={site.url} target="_blank" rel="noopener noreferrer" className={`${cardBase} ${className}`}>
        {inner}
      </a>
    );
  }
  if (site.status === 'partner') {
    return (
      <Link to="/contact?path=hosting" className={`${cardBase} ${className}`}>
        {inner}
      </Link>
    );
  }
  return <div className={`${cardBase} hover:border-secondary-800 ${className}`}>{inner}</div>;
};

const Directory: React.FC = () => {
  const [sortMode, setSortMode] = useState<HostingSortMode>('visitsDesc');
  const [apiCounts, setApiCounts] = useState<Record<string, number>>({});
  const [visitStatus, setVisitStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');

  const sitesWithCounts = useMemo(() => mergeSitesWithStoredVisits(hostedSites, apiCounts), [apiCounts]);

  const fetchCounts = useCallback(() => {
    getHostingVisits()
      .then((res) => {
        const counts = res?.data?.counts;
        if (!counts || typeof counts !== 'object') {
          setApiCounts({});
          setVisitStatus('unavailable');
          return;
        }
        setApiCounts(counts);
        setVisitStatus('ready');
      })
      .catch(() => {
        setApiCounts({});
        setVisitStatus('unavailable');
      });
  }, []);

  useEffect(() => {
    fetchCounts();
    const delayed = window.setTimeout(fetchCounts, 500);
    // Live counter: refresh while the tab is visible so a kept-open dashboard
    // (e.g. watching a hosted site in another window) ticks up without a reload.
    const poll = window.setInterval(fetchCounts, 15000);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchCounts();
    };
    const onFocus = () => fetchCounts();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearTimeout(delayed);
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchCounts]);

  const sortedSites = useMemo(() => {
    const copy = [...sitesWithCounts];
    switch (sortMode) {
      case 'nameAsc':
        return copy.sort((a, b) => a.name.localeCompare(b.name));
      case 'nameDesc':
        return copy.sort((a, b) => b.name.localeCompare(a.name));
      case 'visitsDesc':
        return copy.sort((a, b) => b.monthlyVisits - a.monthlyVisits);
      case 'visitsAsc':
        return copy.sort((a, b) => a.monthlyVisits - b.monthlyVisits);
      default:
        return copy;
    }
  }, [sortMode, sitesWithCounts]);

  const topVisited = useMemo(
    () =>
      sitesWithCounts
        .filter((s) => s.status === 'live' && !!s.url)
        .slice()
        .sort((a, b) => b.monthlyVisits - a.monthlyVisits)
        .slice(0, TOP_VISITED_COUNT),
    [sitesWithCounts],
  );

  return (
    <div className="page-inner pb-10">
      <PageHeader title="Hosted sites" lead="Live and upcoming sites FairCorp hosts. Select a live site to visit it." />

      <section aria-labelledby="top-sites">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h2 id="top-sites" className="text-lg font-bold text-white">
            Top hosted sites
          </h2>
          <span className="text-xs text-secondary-400">By monthly visits</span>
        </div>
        {topVisited.length === 0 ? (
          <p className="panel p-4 text-sm text-secondary-300">Top sites will appear here once visit tracking is enabled.</p>
        ) : (
          <HScroller label="Top hosted sites" trackClassName="gap-3 pr-4">
            {topVisited.map((site) => (
              <div key={site.name} className="snap-start shrink-0 w-64">
                <SiteCard site={site} visitStatus={visitStatus} />
              </div>
            ))}
          </HScroller>
        )}
      </section>

      <section aria-labelledby="all-sites" className="mt-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <h2 id="all-sites" className="text-lg font-bold text-white">
            All hosted sites
          </h2>
          <select
            aria-label="Sort hosted sites"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as HostingSortMode)}
            className="min-h-[44px] px-3 bg-secondary-900 border border-secondary-700 rounded-xl text-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-400 text-sm"
          >
            <option value="nameAsc">Name (A-Z)</option>
            <option value="nameDesc">Name (Z-A)</option>
            <option value="visitsDesc">Monthly visits (high to low)</option>
            <option value="visitsAsc">Monthly visits (low to high)</option>
          </select>
        </div>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sortedSites.map((site) => (
            <li key={site.name}>
              <SiteCard site={site} visitStatus={visitStatus} />
            </li>
          ))}
        </ul>
      </section>

      <aside className="mt-8 panel p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Want a site hosted?</h2>
          <p className="text-sm text-secondary-300">
            Browse the monthly plans, then send a hosting inquiry. Partnership requests are handled on the{' '}
            <a href={`${HUB_URL}/apply/partnership`} className="text-primary-300 underline underline-offset-4 hover:text-primary-200">
              FairCorp Hub
            </a>
            .
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <Link to="/plans" className="btn btn-primary">
            Hosting plans <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <Link to="/contact?path=hosting" className="btn btn-outline">
            Hosting inquiry
          </Link>
        </div>
      </aside>
    </div>
  );
};

export default Directory;
